import type { UserRole } from "../types";
import type {
  WikiPage,
  WikiPageSummary,
  WikiPageTreeNode,
  WikiPageUpsertInput,
  WikiVisibility,
} from "../models/wiki";

export const WIKI_PAGE_STORAGE_BASENAME = "wiki_pages";

export function slugifyWikiTitle(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/['’"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function normalizeWikiTags(tags: unknown) {
  if (!Array.isArray(tags)) return [];
  return Array.from(
    new Set(
      tags
        .map((tag) => String(tag || "").trim())
        .filter(Boolean)
    )
  );
}

export function normalizeWikiStringList(values: unknown) {
  if (!Array.isArray(values)) return [];
  return Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

export function normalizeWikiVisibility(value: unknown): WikiVisibility {
  const normalized = String(value || "").trim().toLowerCase();
  switch (normalized) {
    case "admin_only":
    case "roles":
    case "departments":
    case "hidden":
      return normalized;
    default:
      return "all_staff";
  }
}

export function sanitizeWikiPageInput(
  input: WikiPageUpsertInput,
  options: {
    fallbackSlug?: string;
    fallbackStatus?: WikiPage["status"];
  } = {}
): WikiPageUpsertInput {
  const title = String(input.title || "").trim() || "Untitled Page";
  const slug = slugifyWikiTitle(String(input.slug || "").trim() || title || options.fallbackSlug || "wiki-page");
  return {
    title,
    slug,
    parentId: input.parentId || null,
    order: Number.isFinite(Number(input.order)) ? Number(input.order) : 0,
    content: typeof input.content === "string" ? input.content : "",
    contentFormat:
      input.contentFormat === "mdx" || input.contentFormat === "structured"
        ? input.contentFormat
        : "markdown",
    tags: normalizeWikiTags(input.tags),
    category: String(input.category || "").trim() || "",
    visibility: normalizeWikiVisibility(input.visibility),
    allowedRoles: normalizeWikiStringList(input.allowedRoles) as UserRole[],
    allowedDepartments: normalizeWikiStringList(input.allowedDepartments),
    status:
      input.status === "published" || input.status === "archived"
        ? input.status
        : options.fallbackStatus || "draft",
  };
}

export function buildWikiPageTree(pages: WikiPageSummary[]) {
  const nodes = new Map<string, WikiPageTreeNode>();
  const roots: WikiPageTreeNode[] = [];

  pages.forEach((page) => {
    nodes.set(page.id, {
      ...page,
      children: [],
    });
  });

  Array.from(nodes.values())
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title))
    .forEach((node) => {
      if (node.parentId && nodes.has(node.parentId)) {
        nodes.get(node.parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

  const sortTree = (items: WikiPageTreeNode[]) => {
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));
    items.forEach((item) => sortTree(item.children));
  };

  sortTree(roots);
  return roots;
}

export function extractWikiExcerpt(content: string, maxLength = 180) {
  const plainText = String(content || "")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, (_match, label: string) => label)
    .replace(/:::[\s\S]*?:::/g, " ")
    .replace(/::[\s\S]*?::/g, " ")
    .replace(/[#>*`|_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (plainText.length <= maxLength) return plainText;
  return `${plainText.slice(0, maxLength).trim()}…`;
}

export function canAccessWikiPage(
  page: Pick<WikiPage, "status" | "visibility" | "allowedRoles" | "allowedDepartments">,
  context: {
    role?: string | null;
    department?: string | null;
    isAdmin?: boolean;
  }
) {
  if (page.status === "archived") {
    return Boolean(context.isAdmin);
  }

  if (page.status === "draft" && !context.isAdmin && !page.allowedRoles.includes(context.role as UserRole)) {
    return false;
  }

  switch (page.visibility) {
    case "admin_only":
      return Boolean(context.isAdmin);
    case "roles":
      return page.allowedRoles.length === 0 || page.allowedRoles.includes(context.role as UserRole);
    case "departments": {
      const normalizedDepartment = String(context.department || "").trim().toLowerCase();
      return (
        page.allowedDepartments.length === 0 ||
        page.allowedDepartments.some((department) => department.trim().toLowerCase() === normalizedDepartment)
      );
    }
    case "hidden":
      return Boolean(context.isAdmin);
    default:
      return true;
  }
}

export function buildWikiPageSummary(page: WikiPage): WikiPageSummary {
  const {
    id,
    title,
    slug,
    parentId,
    order,
    tags,
    category,
    visibility,
    allowedRoles,
    allowedDepartments,
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    status,
  } = page;
  return {
    id,
    title,
    slug,
    parentId: parentId || null,
    order,
    tags,
    category,
    visibility,
    allowedRoles,
    allowedDepartments,
    createdAt,
    updatedAt,
    createdBy,
    updatedBy,
    status,
  };
}
